import { ColumnType } from 'antd/es/table';
import { useDispatch } from "react-redux";
import React, { useEffect, useState, useMemo } from "react";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import { TooltipComponent } from "../../components/Component";
import { Drawer, Table, Progress, Tooltip } from 'antd';
import { tasksApis } from '../../api/NewTasks';
import dayjs from 'dayjs';
import reportApis from '../../api/reports';
import {SelectOption} from "../form-components/SelectComponent";

interface TaskItem {
    taskId: string;
    taskName: string;
    taskOwnerName: string;
    taskPlannedStartDate: string;
    taskPlannedEndDate: string;
    priorityName: string;
    completionPercentage: number;
    taskStatus: string;
    uid: string;
}

const WorkCategoryWiseTaskReport = () => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [tasksData, setTasksData] = useState<TaskItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<{ userId: string, userName: string } | null>(null);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [overdueDrawerVisible, setOverdueDrawerVisible] = useState(false);
    const [overdueTasksData, setOverdueTasksData] = useState<TaskItem[]>([]);
    const [WorkCategoriesList, setWorkCategoriesList] = useState<SelectOption[]>(
        []
    );
    const { id: projectId, projectName } = location.state || {};

    const taskColumns: ColumnType<TaskItem>[] = useMemo(() => [

        {
            title: "Work Category",
            dataIndex: "workCategory",
            key: "workCategory",
            width: 180,
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
        },
        {
            title: "Total Tasks",
            dataIndex: "totalTasks",
            key: "totalTasks",
            width: 80,
            render(text: any, record: TaskItem) {
                return (
                    <>
                        <div
                            className="inlineTableCellLink"
                            id={`task-${record?.taskId}`}
                            onClick={() => handleTotalTasksClick(record)}
                        >
                            {text}
                        </div>
                        <TooltipComponent
                            iconClass="info-circle text-info"
                            icon={""}
                            id={`task-${record?.taskId}`}
                            direction="right"
                            text={"Click To View " + text + " Details"}
                            containerClassName={"mt-1"}
                        />
                    </>
                );
            },
        },
        {
            title: "Delayed Tasks",
            dataIndex: "delayedTasks",
            key: "delayedTasks",
            width: 80,
            render(text: any, record: any) {
                return (
                    <>
                        <div
                            className="inlineTableCellLink"
                            id={`phase-${record?.phaseId}`}
                            onClick={() => handleDelayTasksClick(record)}
                        >
                            {text}
                        </div>
                        <TooltipComponent
                            iconClass="info-circle text-info"
                            icon={""}
                            id={`phase-${record?.phaseId}`}
                            direction="right"
                            text={"Click To View " + text + " Details"}
                            containerClassName={"mt-1"}
                        />
                    </>
                );
            },
        },
        {
            title: "Completed Percentage",
            dataIndex: "completedPercentage",
            key: "completedPercentage",
            width: 200,
            render: (text, record) => {
                if (text === null || text === undefined || text === "") return "N/A";
                
                const percentage = parseFloat(text.toString().replace('%', ''));
                
                return (
                    <Tooltip
                        title={`Completion: ${text}`}
                        placement="top"
                    >
                        <Progress
                            strokeLinecap="butt"
                            size={[186, 10]}
                            percent={percentage}
                            status="active"
                            strokeColor={{
                                from: "#87d068",
                                to: "#87d068",
                            }}
                        />
                    </Tooltip>
                );
            }
        },
        {
            title: "Avg.Completion Time",
            dataIndex: "avgCompletionDays",
            key: "avgCompletionDays",
            width: 100,
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : `${text} Days`)
        },
        {
            title: "Owner With Most Tasks",
            dataIndex: "assignedTo",
            key: "assignedTo",
            width: 150,
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
        },
    ], [dispatch]);

    const overdueTaskColumns: ColumnType<TaskItem>[] = useMemo(() => [
        {
            title: "Task Name",
            dataIndex: "taskName",
            key: "taskName",
            width: 200,
            fixed: "left",
            render(text: any, record: any) {
                return (
                    <>
                        <span
                            className="inlineTableCellLink"
                            id={`task-${record?.taskId}`}
                            onClick={() => {
                                navigate(`/pms/project/task-detail/${record.taskId}`, {
                                    state: {
                                        projectId,
                                        projectName,
                                        returnPath: location.pathname,
                                        returnState: location.state,
                                    },
                                });
                            }}
                        >
                            {text}
                        </span>
                    </>
                );
            },
        },
        {
            title: "Planned Start Date",
            dataIndex: "plannedStartDate",
            key: "plannedStartDate",
            width: 150,
            render: (text: string) => text || "N/A"
        },
        {
            title: "Planned End Date",
            dataIndex: "plannedEndDate",
            key: "plannedEndDate",
            width: 150,
            render: (text: string) => text || "N/A"
        },
        {
            title: "Actual Start Date",
            dataIndex: "actualStartDate",
            key: "actualStartDate",
            width: 150,
            render: (text: string) => text || "N/A"
        },
        {
            title: "Actual End Date",
            dataIndex: "actualEndDate",
            key: "actualEndDate",
            width: 150,
            render: (text: string) => text || "N/A"
        },
        {
            title: "Owner",
            dataIndex: "userName",
            key: "userName",
            width: 150,
        },
        {
            title: "Delayed Status",
            dataIndex: "delayedStatus",
            key: "delayedStatus",
            width: 150,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Task Status",
            dataIndex: "taskStatus",
            key: "taskStatus",
            width: 150,
        },
        {
            title: "Completion %",
            dataIndex: "completedPercentage",
            key: "completedPercentage",
            width: 120,
            render: (text: number) => `${text}%` || "N/A",
        },
    ], [dispatch]);

    const handleTotalTasksClick = async (record: any) => {
        setSelectedUser({
            userId: record.userId,
            userName: record.assigedTo
        });
        setLoading(true);
        setDrawerVisible(true);

        try {
            const statusForApi = record.taskStatus === "Active" ? "Open" : record.taskStatus;
            const response = await tasksApis.listTasks({
                page: 1,
                pageSize: 10,
                projectIds: projectId,
                pageDiff: 0,
                sortOrder: "DESC",
                direction: "P",
                taskStatus: statusForApi,
                // reportName: "WorkCategoryWiseTaskReport",
                workCategoryIds: record.workCategoryId,
                taskOwnerIds: record.assignedId,
            });
            const { status, data } = response || {};
            if (status) {
                const { count, list, projectResponse } = data || {}
                setTasksData(list || []);
            }

        } catch (error) {
            console.error("Error fetching tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelayTasksClick = async (record: any) => {
        setSelectedUser({
            userId: record.assigedId,
            userName: record.assigedTo
        });
        setLoading(true);
        setOverdueDrawerVisible(true);

        try {
            const statusForApi = record.taskStatus === "Active" ? "Open" : record.taskStatus;
            const response = await reportApis.fetchPMSReports({
                page: 1,
                pageSize: 10,
                projectIds: projectId,
                pageDiff: 0,
                sortOrder: "DESC",
                direction: "P",
                taskStatus: statusForApi,
                reportName: "overDueWorkCategoryWiseTaskReport",
                workCategoryIds: record.workCategoryId,
                taskOwnerIds: record.assignedId,
            });

            const { status, data } = response || {};
            if (status) {
                setOverdueTasksData(data?.list || []);
            }
        } catch (error) {
            console.error("Error fetching overdue tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchWorkCategoriesList = async () => {
        try {
            const response: any = await tasksApis.fetchWorkCatgoriesList();
            const { data, status } = response || {};
            if (status) {
                const formatted = data.map(
                    (item: { workCategoryName: any; workCategoryId: any }) => ({
                        label: item.workCategoryName,
                        value: item.workCategoryId,
                    })
                );
                setWorkCategoriesList(formatted);
            }
        } catch (error) {
            console.error("Error fetching work categories:", error);
        }
    };

    useEffect(() => {
        fetchWorkCategoriesList();
        dispatch(
            updateMenuData([
                {
                    text: "Work Category-Wise Task Performance Report",
                    link: "pms/work-category-wise-task-performance-report",
                },
            ])
        );
    }, []);

    const drawerColumns: ColumnType<TaskItem>[] = useMemo(() => [
        {
            title: "Task Name",
            dataIndex: "taskName",
            key: "taskName",
            width: 250,
            fixed: "left",
            render(text: any, record: any) {
                return (
                    <>
                        <span
                            className="inlineTableCellLink"
                            id={`task-${record?.taskId}`}
                            onClick={() => {
                                navigate(`/pms/project/task-detail/${record.taskId}`, {
                                    state: {
                                        projectId,
                                        projectName,
                                        returnPath: location.pathname,
                                        returnState: location.state,
                                    },
                                });
                            }}
                        >
                            {text}
                        </span>
                    </>
                );
            },
        },
        {
            title: "Owner",
            dataIndex: "taskOwnerName",
            key: "taskOwnerName",
            width: 250,
        },

        {
            title: "Start Date",
            dataIndex: "taskPlannedStartDate",
            key: "taskPlannedStartDate",
            width: 250,
            render: (text: string) => text || "N/A"
        },
        {
            title: "End Date",
            dataIndex: "taskPlannedEndDate",
            key: "taskPlannedEndDate",
            width: 250,
            render: (text: string) => text || "N/A"
        },
        {
            title: "Priority",
            dataIndex: "priorityName",
            key: "priorityName",
            width: 250,
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
        },
        {
            title: "Status",
            dataIndex: "taskStatus",
            key: "taskStatus",
            width: 200,
            fixed: "right",
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
        },
    ], [handleTotalTasksClick]);

    return (
        <>
            <Template1
                moduleKey={window.location.pathname}
                columns={taskColumns}
                pageTitle='Work Category-wise Task Report'
                pageDescription=''
                previousPath={{
                    link: "/pms/project/reports",
                    state: location.state,
                }}
                filters={{
                    customFilters: [
                        {
                            type: "multiple_selection",
                            name: "workCategoryId",
                            label: "Work Category",
                            options: WorkCategoriesList,
                        },
                    ]
                }}
            />
            <Drawer
                title={`Tasks for ${selectedUser?.userName || 'User'}`}
                width={1200}
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Table
                    columns={drawerColumns}
                    dataSource={tasksData}
                    loading={loading}
                    scroll={{ x: 1300 }}
                    rowKey="taskId"
                />
            </Drawer>

            <Drawer
                title={`Delayed Tasks for ${selectedUser?.userName || 'User'}`}
                width={1200}
                onClose={() => setOverdueDrawerVisible(false)}
                open={overdueDrawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Table
                    columns={overdueTaskColumns}
                    dataSource={overdueTasksData}
                    loading={loading}
                    scroll={{ x: 1500 }}
                    rowKey="taskId"
                />
            </Drawer>
        </>
    );
}

export default WorkCategoryWiseTaskReport;